import type { DaySection } from '../api/types'
import { flight, smooth, type Camera, type FilmScript, type Frame, type ScriptState } from './filmScript'

/**
 * The camera of the day film (#139, 1.118.0) -- pure arithmetic, no DOM,
 * held by `tools/pruefstand/tourscript.ts`.
 *
 * **Since 1.125.0 the one camera of every film.** Until then it was the
 * second of "2 komplett unabhängige Wege"; from use came "Codebasis
 * ‚einfach' und ‚komplex' zusammenlegen". With nothing chosen (no following,
 * no tilt) it is the simple script's camera frame for frame. The simple
 * script (`filmScript()`) is read here, never changed. The time line --
 * when a section moves, where the vehicle is, which pins stand, the stops,
 * the length -- comes from it as it is, so there is one time line and not
 * two. What this file adds is only the camera: it follows the vehicle (A),
 * it tilts (B), and it carries a rotation, so that the direction of travel
 * runs across a 16:9 picture: west to the left, east to the right, and the
 * map never more than `MOST_ROTATION_DEG` from north up.
 *
 * The beginning and the end stay the whole day, flat and north up, as in the
 * simple film: whoever watches sees first where the day goes.
 */

/** The camera of the complex film: the simple one plus a rotation and a tilt, both in radians. */
export interface TourCamera extends Camera {
  /** The map is turned by this angle around the centre (screen clockwise is positive). */
  rotation: number
  /** 0 looks straight down; `TILT` is the oblique view. */
  tilt: number
}

export interface TourWish {
  /** A: the camera follows the vehicle. */
  follow: boolean
  /** B: the oblique view. */
  tilt: boolean
}

export type TourState = Omit<ScriptState, 'camera'> & { camera: TourCamera }

export interface TourScript {
  /** The simple film's script it reads: lines, pins, the whole day. */
  base: FilmScript
  duration: number
  whole: TourCamera
  /** When each section moves, in seconds of the film. */
  legs: { start: number; end: number }[]
  at(t: number): TourState
}

/** The oblique view: 45 degrees from straight down -- steeper than a flight simulator, so that the far edge is still map and not sky. */
export const TILT = (45 * Math.PI) / 180
/** The vertical angle of view of the oblique camera. */
export const VERTICAL_FOV = (40 * Math.PI) / 180
/**
 * Following, the camera is this many zoom levels closer than the section's
 * own view. One level is twice the speed over the picture of the simple
 * film; 1.118.0 had two, four times the speed, and from use came "im
 * Vergleich zum einfachen etwas zu hektisch. die Geschwindigkeiten sind
 * auch schneller" -- both films were measured at the same length.
 */
export const FOLLOW_EXTRA = 1
/** Following, the vehicle stands at this share of the picture's width (going west at one minus it): more of the way ahead is in view. */
export const FOLLOW_AT = 0.38
/** The direction of travel is taken over this many seconds before and after the moment. */
export const HEADING_WINDOW_S = 3
/** The turn of the picture at most, degrees per second -- a road that doubles back would otherwise spin it (60 until 1.119.0, "zu hektisch"). */
export const MOST_TURN_DEG_S = 30
/**
 * The map is turned at most this far from north up (1.120.0). Until then
 * the way always pointed to the right, and a flight from Frankfurt to New
 * York turned the map by 180 degrees -- from use: "das Flugzeug fliegt in
 * die falsche Richtung, die Karte steht auf dem Kopf". Now a way west runs
 * to the left (the side comes from the simple script's `westward`, with its
 * calm), a way east to the right, and north or south the picture leans by
 * this much and no more.
 */
export const MOST_ROTATION_DEG = 45
/**
 * A change of side (west to east or back) moves the vehicle across the
 * picture over at least this long. The smoothing of the way alone makes it
 * 0.98 % of the width per frame; with three seconds it is 0.71 %.
 */
export const SIDE_SWITCH_S = 3
/**
 * Following, the camera's way is smoothed with this time constant, forwards
 * and then backwards, so that it does not trail the vehicle (1.119.0). It
 * no longer follows every bend; the vehicle sways a little around its place.
 * 0.8 until 1.139.0; from use: "geht es noch etwas ruhiger?" -- since
 * 1.140.0 the rotation is smoothed with the same bell, not only the place.
 */
export const FOLLOW_SMOOTH_S = 2
/** ... but never wider than the section divided by this: a short section keeps the vehicle in the picture. */
export const FOLLOW_SMOOTH_SHARE = 8
/**
 * The vehicle's heading is smoothed over a bell with this sigma (1.140.0).
 * The simple script gives one heading per stretch of the track, so the 3D
 * model (and a top-view symbol) snapped round at every point of a winding
 * road; from use: "geht es noch etwas ruhiger?". Averaged as unit vectors,
 * so that a heading across ±180 degrees does not spin the other way.
 */
export const VEHICLE_TURN_SMOOTH_S = 0.5
/**
 * Tilting and straightening take at least this long (1.119.0). A flight of
 * the simple script may be 1.8 s; the tilt then goes on into the first
 * seconds of the section, so no section and not the film gets longer.
 */
export const TILT_RAMP_S = 2.5
/** A section without a flight before it blends in over this long. */
export const BLEND_S = 1.5
/** Tilted without following: this much further out, so that the near corners keep the way. */
export const TILT_ZOOM_DROP = 0.4

const TILE = 256

const flat = (camera: Camera): TourCamera => ({ x: camera.x, y: camera.y, zoom: camera.zoom, rotation: 0, tilt: 0 })

/** The shortest way from angle a to angle b. */
function turnBetween(a: number, b: number): number {
  let d = (b - a) % (Math.PI * 2)
  if (d > Math.PI) d -= Math.PI * 2
  if (d < -Math.PI) d += Math.PI * 2
  return d
}

/** An angle in (-π, π]. */
function wrapAngle(a: number): number {
  const w = a % (Math.PI * 2)
  return w > Math.PI ? w - Math.PI * 2 : w <= -Math.PI ? w + Math.PI * 2 : w
}

/** Between two cameras, eased like the simple film's flights: the place and zoom on its path, rotation and tilt alongside. */
function tourFlight(a: TourCamera, b: TourCamera, u: number, frame: Frame, maxZoom: number): TourCamera {
  const base = flight(a, b, u, frame, maxZoom)
  const e = smooth(u)
  return { ...base, rotation: a.rotation + turnBetween(a.rotation, b.rotation) * e, tilt: a.tilt + (b.tilt - a.tilt) * e }
}

/** A plain blend of two cameras, for a section without a flight before it. */
function blend(a: TourCamera, b: TourCamera, u: number): TourCamera {
  const e = smooth(u)
  return {
    x: a.x + (b.x - a.x) * e,
    y: a.y + (b.y - a.y) * e,
    zoom: a.zoom + (b.zoom - a.zoom) * e,
    rotation: a.rotation + turnBetween(a.rotation, b.rotation) * e,
    tilt: a.tilt + (b.tilt - a.tilt) * e,
  }
}

/**
 * When each section moves (`drawn` leaves 0 and reaches 1), found on the
 * simple script itself: scanned frame by frame, then halved down to a
 * millisecond. Read and not computed again, so the two films cannot
 * disagree about it.
 */
function legTimes(base: FilmScript, step: number): { start: number; end: number }[] {
  const out = base.lines.map(() => ({ start: base.duration, end: base.duration }))
  const edge = (i: number, test: (share: number) => boolean): number => {
    let hi = -1
    for (let t = 0; t <= base.duration + step / 2; t += step) {
      if (test(base.at(t).drawn[i] ?? 0)) {
        hi = t
        break
      }
    }
    if (hi < 0) return base.duration
    let lo = Math.max(0, hi - step)
    if (test(base.at(lo).drawn[i] ?? 0)) return lo
    for (let k = 0; k < 20; k++) {
      const mid = (lo + hi) / 2
      if (test(base.at(mid).drawn[i] ?? 0)) hi = mid
      else lo = mid
    }
    return hi
  }
  base.lines.forEach((_, i) => {
    out[i] = { start: edge(i, (s) => s > 0), end: edge(i, (s) => s >= 1) }
  })
  return out
}

/**
 * The following camera of one section, one entry per frame: centre ahead
 * of the vehicle, rotation from the smoothed way, turned no faster than
 * `MOST_TURN_DEG_S`. Computed once, because a limit on the turn needs the
 * frame before -- `at()` is asked in any order.
 */
function followTrack(base: FilmScript, start: number, end: number, zoom: number, frame: Frame, fps: number): TourCamera[] {
  const where = (t: number): [number, number] => {
    const v = base.at(Math.max(start, Math.min(end, t))).vehicle
    return v === null ? [base.whole.x, base.whole.y] : [v.x, v.y]
  }
  const count = Math.max(2, Math.ceil((end - start) * fps) + 1)
  const times = Array.from({ length: count }, (_, i) => start + ((end - start) * i) / (count - 1))
  // The way's direction around each moment; where the vehicle stands (a
  // stop, a short walk) the window widens until it moves.
  const [fx, fy] = where(start)
  const [lx, ly] = where(end)
  const whole = Math.atan2(ly - fy, lx - fx)
  const raw = times.map((t) => {
    for (const w of [HEADING_WINDOW_S, HEADING_WINDOW_S * 3, end - start]) {
      const [ax, ay] = where(t - w)
      const [bx, by] = where(t + w)
      if (Math.hypot(bx - ax, by - ay) > 1e-12) return Math.atan2(by - ay, bx - ax)
    }
    return whole
  })
  // West to the left, east to the right: the map turns against the way,
  // but never further than `MOST_ROTATION_DEG` from north up.
  const westward = times.map((t) => base.at(Math.max(start, Math.min(end, t))).vehicle?.westward === true)
  const leanMost = (MOST_ROTATION_DEG * Math.PI) / 180
  const wanted = raw.map((h, i) => Math.max(-leanMost, Math.min(leanMost, wrapAngle((westward[i] ? Math.PI : 0) - h))))
  const step = (end - start) / Math.max(1, count - 1)
  // The side the camera looks ahead to: +1 right, -1 left, changed no faster
  // than over `SIDE_SWITCH_S`, so the vehicle glides across instead of jumping.
  const sides: number[] = westward.map((w) => (w ? -1 : 1))
  const sideStep = (2 * step) / SIDE_SWITCH_S
  for (let i = 1; i < count; i++) sides[i] = sides[i - 1]! + Math.max(-sideStep, Math.min(sideStep, sides[i]! - sides[i - 1]!))
  for (let i = count - 2; i >= 0; i--) sides[i] = sides[i + 1]! + Math.max(-sideStep, Math.min(sideStep, sides[i]! - sides[i + 1]!))
  const most = ((MOST_TURN_DEG_S * Math.PI) / 180) * step
  const limited = [...wanted]
  for (let i = 1; i < count; i++) {
    const d = turnBetween(limited[i - 1]!, limited[i]!)
    limited[i] = limited[i - 1]! + Math.max(-most, Math.min(most, d))
  }
  for (let i = count - 2; i >= 0; i--) {
    const d = turnBetween(limited[i + 1]!, limited[i]!)
    limited[i] = limited[i + 1]! + Math.max(-most, Math.min(most, d))
  }
  const ahead = ((0.5 - FOLLOW_AT) * frame.width) / (TILE * 2 ** zoom)
  const raw2 = times.map((t, i) => {
    const [vx, vy] = where(t)
    const r = limited[i]!
    // The picture's right on the map is the direction the rotation turns to the right.
    const a = ahead * sides[i]!
    return { x: vx + Math.cos(-r) * a, y: vy + Math.sin(-r) * a, zoom, rotation: r, tilt: 0 }
  })
  // The bell no wider than a share of the section (1.140.0): with two
  // seconds of bell on a section of two, the camera stopped following and
  // the vehicle left the picture (`gelaende.mjs`, y 562 of 360).
  return smoothedWay(raw2, step, Math.min(FOLLOW_SMOOTH_S, (end - start) / FOLLOW_SMOOTH_SHARE))
}

/**
 * The camera's way, smoothed with a bell over the neighbouring frames
 * (sigma `FOLLOW_SMOOTH_S`). At both ends the way is mirrored through the
 * end point, so a steady drive stays exactly where it was -- the vehicle
 * keeps its place at `FOLLOW_AT` -- and both ends stay put: the flights
 * before and after a section meet the camera there. Only the wiggles of a
 * winding road go.
 */
function smoothedWay(track: TourCamera[], step: number, seconds: number): TourCamera[] {
  const n = track.length - 1
  if (n < 2) return track
  const sigma = Math.max(1, seconds / step)
  const reach = Math.ceil(sigma * 3)
  const weights = Array.from({ length: reach * 2 + 1 }, (_, k) => Math.exp(-(((k - reach) / sigma) ** 2) / 2))
  const total = weights.reduce((a, b) => a + b, 0)
  const pass = (values: number[]): number[] => {
    const at = (j: number): number => {
      if (j < 0) return 2 * values[0]! - values[Math.min(n, -j)]!
      if (j > n) return 2 * values[n]! - values[Math.max(0, 2 * n - j)]!
      return values[j]!
    }
    return values.map((_, i) => {
      if (i === 0 || i === n) return values[i]!
      let sum = 0
      for (let k = -reach; k <= reach; k++) sum += weights[k + reach]! * at(i + k)
      return sum / total
    })
  }
  const xs = pass(track.map((c) => c.x))
  const ys = pass(track.map((c) => c.y))
  // The rotation too (1.140.0): limited in speed alone, it started and
  // stopped turning at once. `followTrack()` builds it without a jump at
  // 2π, so its values can be smoothed as they are.
  const rs = pass(track.map((c) => c.rotation))
  return track.map((c, i) => ({ ...c, x: xs[i]!, y: ys[i]!, rotation: rs[i]! }))
}

/** A camera out of a per-frame track, between its two nearest entries. */
function fromTrack(track: TourCamera[], start: number, end: number, t: number): TourCamera {
  const u = end > start ? Math.max(0, Math.min(1, (t - start) / (end - start))) * (track.length - 1) : 0
  const i = Math.min(track.length - 2, Math.floor(u))
  const a = track[i]!
  const b = track[Math.min(track.length - 1, i + 1)]!
  const f = u - i
  return {
    x: a.x + (b.x - a.x) * f,
    y: a.y + (b.y - a.y) * f,
    zoom: a.zoom,
    rotation: a.rotation + turnBetween(a.rotation, b.rotation) * f,
    tilt: 0,
  }
}

/**
 * The complex film's script over the simple one. `null` never: whoever
 * has a simple script has sections.
 */
export function tourScript(base: FilmScript, frame: Frame, wish: TourWish, maxZoom: number, fps = 30): TourScript {
  const whole = flat(base.whole)
  const legs = legTimes(base, 1 / fps)
  const tilt = wish.tilt ? TILT : 0

  // The section's own view, the simple film's camera in the middle of its motion.
  const views = legs.map(({ start, end }) => base.at((start + end) / 2).camera)
  const tracks = legs.map(({ start, end }, i) => {
    if (!wish.follow) return null
    const zoom = Math.min(maxZoom, Math.max(views[i]!.zoom, views[i]!.zoom + FOLLOW_EXTRA))
    return followTrack(base, start, end, zoom, frame, fps)
  })
  const inLeg = (i: number, t: number): TourCamera => {
    const track = tracks[i]
    if (track !== null && track !== undefined) return { ...fromTrack(track, legs[i]!.start, legs[i]!.end, t), tilt }
    const v = views[i]!
    return { x: v.x, y: v.y, zoom: wish.tilt ? Math.max(0, v.zoom - TILT_ZOOM_DROP) : v.zoom, rotation: 0, tilt }
  }
  const backEnd = Math.max(0, base.duration - base.outro)
  // Back to the whole day. Where the simple film needs no flight (its
  // section is the whole day), the turned or tilted picture still has to
  // come back: then into the closing stand, at least `BLEND_S`.
  const lastLeg = legs[legs.length - 1]
  const home = lastLeg === undefined ? 0 : Math.max(backEnd, Math.min(base.duration, lastLeg.end + BLEND_S))

  const cameraAt = (t: number): TourCamera => {
    if (legs.length === 0) return whole
    const first = legs[0]!
    if (t < first.start) {
      if (t <= base.intro || first.start <= base.intro) return whole
      return tourFlight(whole, inLeg(0, first.start), (t - base.intro) / (first.start - base.intro), frame, maxZoom)
    }
    for (let i = 0; i < legs.length; i++) {
      const leg = legs[i]!
      if (t >= leg.start && t <= leg.end) {
        const here = inLeg(i, t)
        // No flight before it (two views nearly the same): blended in, so the
        // picture does not jump. The wait at a change of vehicle is no flight.
        const gap = i === 0 ? first.start - base.intro : leg.start - legs[i - 1]!.end - (base.holds[i] ?? 0)
        if (gap < 0.05 && t - leg.start < BLEND_S) {
          return blend(i === 0 ? whole : inLeg(i - 1, legs[i - 1]!.end), here, (t - leg.start) / BLEND_S)
        }
        return here
      }
      const next = legs[i + 1]
      if (next !== undefined && t > leg.end && t < next.start) {
        // The wait at a change of vehicle (1.150.0) before the flight: the
        // camera stays where the section ended, as in the simple film.
        const from = Math.min(next.start, leg.end + (base.holds[i + 1] ?? 0))
        if (t <= from) return inLeg(i, leg.end)
        return tourFlight(inLeg(i, leg.end), inLeg(i + 1, next.start), (t - from) / (next.start - from), frame, maxZoom)
      }
    }
    const last = legs[legs.length - 1]!
    if (t >= home || home <= last.end) return whole
    return tourFlight(inLeg(legs.length - 1, last.end), whole, (t - last.end) / (home - last.end), frame, maxZoom)
  }

  // The tilt over its own ramp (1.119.0): it begins with the first flight
  // and takes at least `TILT_RAMP_S`, going on into the section when the
  // flight is shorter; the straightening ends where the camera is home.
  const first = legs[0]
  const riseFrom = first === undefined ? 0 : Math.min(base.intro, first.start)
  const riseTo = first === undefined ? 0 : riseFrom + Math.max(TILT_RAMP_S, first.start - riseFrom)
  const fallFrom = lastLeg === undefined ? 0 : Math.min(lastLeg.end, home - TILT_RAMP_S)
  const tiltAt = (t: number): number => {
    if (!wish.tilt || first === undefined) return 0
    const rise = smooth((t - riseFrom) / Math.max(1e-6, riseTo - riseFrom))
    const fall = 1 - smooth((t - fallFrom) / Math.max(1e-6, home - fallFrom))
    return TILT * Math.min(rise, fall)
  }

  // The vehicle's heading over a bell inside its own section (1.140.0): a
  // moment of the section before or after would mix in another vehicle.
  const sigma = VEHICLE_TURN_SMOOTH_S
  const headingStep = 2 / fps
  const calmHeading = (time: number, raw: number): number => {
    const leg = legs.find((l) => time >= l.start && time <= l.end)
    if (leg === undefined || sigma <= 0) return raw
    let cx = 0
    let cy = 0
    for (let d = -3 * sigma; d <= 3 * sigma + 1e-9; d += headingStep) {
      const v = base.at(Math.max(leg.start, Math.min(leg.end, time + d))).vehicle
      if (v === null) continue
      const w = Math.exp(-((d / sigma) ** 2) / 2)
      cx += w * Math.cos(v.heading)
      cy += w * Math.sin(v.heading)
    }
    return Math.hypot(cx, cy) < 1e-9 ? raw : Math.atan2(cy, cx)
  }

  return {
    base,
    duration: base.duration,
    whole,
    legs,
    at: (t) => {
      const time = Math.max(0, Math.min(base.duration, t))
      const raw = base.at(t)
      const state = raw.vehicle === null ? raw : { ...raw, vehicle: { ...raw.vehicle, heading: calmHeading(time, raw.vehicle.heading) } }
      // Nothing chosen (1.125.0): the camera of the simple script, frame for
      // frame -- the film as it was before the two ways became one. Not the
      // view of each section below: the simple script moves between them.
      if (!wish.follow && !wish.tilt) return { ...state, camera: flat(state.camera) }
      return { ...state, camera: { ...cameraAt(time), tilt: tiltAt(time) } }
    },
  }
}

// --- The projection --------------------------------------------------------
//
// The ground is the map in "ground pixels": Mercator, minus the camera's
// centre, times the scale of its zoom, turned by its rotation. A tilted
// camera looks at the centre from behind and above, at the distance where
// one ground pixel is one picture pixel in the middle row -- so a tilt
// changes the perspective and not the scale of what is in the middle.

/** The focal length in picture pixels. */
export function focal(frame: Frame): number {
  return frame.height / 2 / Math.tan(VERTICAL_FOV / 2)
}

/** A Mercator point in ground pixels of a camera (x to the right of the picture, y down it). */
export function toGround(x: number, y: number, camera: TourCamera): [number, number] {
  const scale = TILE * 2 ** camera.zoom
  const dx = (x - camera.x) * scale
  const dy = (y - camera.y) * scale
  const c = Math.cos(camera.rotation)
  const s = Math.sin(camera.rotation)
  return [dx * c - dy * s, dx * s + dy * c]
}

/** Ground pixels back to Mercator. */
export function fromGround(gx: number, gy: number, camera: TourCamera): [number, number] {
  const scale = TILE * 2 ** camera.zoom
  const c = Math.cos(camera.rotation)
  const s = Math.sin(camera.rotation)
  return [camera.x + (gx * c + gy * s) / scale, camera.y + (-gx * s + gy * c) / scale]
}

/**
 * A ground point in the picture. `scale` is the size of a ground pixel
 * there (1 in the middle row, less towards the far edge); `null` behind the
 * horizon.
 */
export function projectGround(gx: number, gy: number, camera: TourCamera, frame: Frame): { x: number; y: number; scale: number } | null {
  const f = focal(frame)
  const depth = f - gy * Math.sin(camera.tilt)
  if (depth <= f * 0.05) return null
  return { x: frame.width / 2 + (f * gx) / depth, y: frame.height / 2 + (f * gy * Math.cos(camera.tilt)) / depth, scale: f / depth }
}

/** A Mercator point in the picture. */
export function project(x: number, y: number, camera: TourCamera, frame: Frame): { x: number; y: number; scale: number } | null {
  const [gx, gy] = toGround(x, y, camera)
  return projectGround(gx, gy, camera, frame)
}

/**
 * Which ground row a picture row shows, and how many ground pixels one
 * picture pixel there is wide. `v` counts from the middle row, down positive.
 * Exact for a flat ground and no roll -- the rows of the picture are rows
 * of the turned ground, which is why the tilted picture can be drawn as
 * strips with a plain 2D canvas.
 */
export function rowSource(v: number, camera: TourCamera, frame: Frame): { gy: number; k: number } {
  const f = focal(frame)
  const sin = Math.sin(camera.tilt)
  const cos = Math.cos(camera.tilt)
  const gy = (v * f) / (f * cos + v * sin)
  return { gy, k: (f - gy * sin) / f }
}

/** The ground the picture shows, in ground pixels: from the far row to the near one. */
export function visibleGround(camera: TourCamera, frame: Frame): { x0: number; x1: number; y0: number; y1: number } {
  const top = rowSource(-frame.height / 2, camera, frame)
  const bottom = rowSource(frame.height / 2, camera, frame)
  const half = (frame.width / 2) * Math.max(top.k, bottom.k)
  return { x0: -half, x1: half, y0: top.gy, y1: bottom.gy }
}

/**
 * The tiles a camera of the complex film needs: those that touch the turned
 * and tilted ground, at the zoom nearest to its own, `coarser` levels less --
 * the same level rule as `tilesFor()`. **Touch, not the box around it**:
 * turned by 45 degrees and tilted, the box is three times the ground, and
 * every tile in it counts against `MAX_TILES`.
 */
export function tourTilesFor(camera: TourCamera, frame: Frame, maxZoom: number, coarser = 0): { z: number; tiles: [number, number][] } {
  const z = Math.max(0, Math.min(maxZoom, Math.round(camera.zoom)) - coarser)
  const n = 2 ** z
  const g = visibleGround(camera, frame)
  // The near edge is narrower than the far one: the ground is a trapezoid.
  const near = (frame.width / 2) * rowSource(frame.height / 2, camera, frame).k
  const far = (frame.width / 2) * rowSource(-frame.height / 2, camera, frame).k
  const quad = ([[-far, g.y0], [far, g.y0], [near, g.y1], [-near, g.y1]] as const).map(([gx, gy]) => {
    const [x, y] = fromGround(gx, gy, camera)
    return [x * n, y * n] as [number, number]
  })
  const xs = quad.map(([x]) => x)
  const ys = quad.map(([, y]) => y)
  const tiles: [number, number][] = []
  for (let x = Math.floor(Math.min(...xs)); x <= Math.floor(Math.max(...xs)); x++) {
    for (let y = Math.max(0, Math.floor(Math.min(...ys))); y <= Math.min(n - 1, Math.floor(Math.max(...ys))); y++) {
      if (touches(quad, x, y)) tiles.push([x, y])
    }
  }
  return { z, tiles }
}

/** Whether the unit square at (x, y) and a convex quadrilateral overlap (separating axes). */
function touches(quad: [number, number][], x: number, y: number): boolean {
  const square: [number, number][] = [[x, y], [x + 1, y], [x + 1, y + 1], [x, y + 1]]
  const axes: [number, number][] = [[1, 0], [0, 1]]
  for (let i = 0; i < quad.length; i++) {
    const [ax, ay] = quad[i]!
    const [bx, by] = quad[(i + 1) % quad.length]!
    axes.push([by - ay, ax - bx])
  }
  for (const [nx, ny] of axes) {
    const range = (points: [number, number][]) => {
      const d = points.map(([px, py]) => px * nx + py * ny)
      return [Math.min(...d), Math.max(...d)] as const
    }
    const [a0, a1] = range(quad)
    const [b0, b1] = range(square)
    if (a1 < b0 || b1 < a0) return false
  }
  return true
}

// --- C: the profile --------------------------------------------------------

export interface Profile {
  /** Metres from the start of the day, per point of every section. */
  at: number[]
  /** Metres above the sea at those points; `null` where a section has no height. */
  height: (number | null)[]
  total: number
  low: number
  high: number
}

/**
 * The heights of the day along its way (#139), or `null` when no section
 * has one. The film's sections are the ones with a line (two points or
 * more), in the order of the script -- the same filter as `filmScript()`.
 *
 * **One entry per height of the server, not per point of the line.** The
 * stored points are thinned: a straight road keeps its two ends, and a
 * profile from them would be one straight slope over a pass. The server's
 * heights carry the section's seconds; each is placed on the way by the
 * point times around it, as the vehicle passes it.
 */
export function profileOf(base: FilmScript, sections: DaySection[]): Profile | null {
  const moving = sections.filter((s) => s.points.length >= 2)
  const at: number[] = []
  const height: (number | null)[] = []
  let offset = 0
  let any = false
  base.lines.forEach((line, i) => {
    const heights = moving[i]?.elevation ?? []
    const points = moving[i]?.points ?? []
    if (heights.length === 0 || points.length !== line.sofar.length) {
      // No height here: a gap in the profile, as long as the section.
      at.push(offset, offset + line.length)
      height.push(null, null)
    } else {
      any = true
      for (const [second, metres] of heights) {
        at.push(offset + metresAtSecond(points, line.sofar, second))
        height.push(metres)
      }
    }
    offset += line.length
  })
  if (!any) return null
  const known = height.filter((h): h is number => h !== null)
  return { at, height, total: offset, low: Math.min(...known), high: Math.max(...known) }
}

/** How far along a section the vehicle is at a second of it, from the times of its points. */
function metresAtSecond(points: [number, number, number][], sofar: number[], second: number): number {
  if (second <= points[0]![2]) return 0
  for (let i = 1; i < points.length; i++) {
    const b = points[i]![2]
    if (second <= b) {
      const a = points[i - 1]![2]
      const u = b > a ? (second - a) / (b - a) : 1
      return sofar[i - 1]! + (sofar[i]! - sofar[i - 1]!) * u
    }
  }
  return sofar[sofar.length - 1]!
}

/** The height at a place of the profile, between its two entries; `null` in a gap. */
export function heightAtPlace(profile: Profile, metres: number): number | null {
  const { at, height } = profile
  if (at.length === 0) return null
  if (metres <= at[0]!) return height[0] ?? null
  for (let i = 1; i < at.length; i++) {
    if (metres <= at[i]!) {
      const a = height[i - 1] ?? null
      const b = height[i] ?? null
      if (a === null || b === null) return a ?? b
      const span = at[i]! - at[i - 1]!
      return span > 0 ? a + ((b - a) * (metres - at[i - 1]!)) / span : b
    }
  }
  return height[height.length - 1] ?? null
}

/** Where on the profile the vehicle is: metres from the start of the day. */
export function profilePosition(base: FilmScript, state: Pick<ScriptState, 'drawn'>): number {
  let metres = 0
  base.lines.forEach((line, i) => {
    metres += (state.drawn[i] ?? 0) * line.length
  })
  return metres
}
